import {
  AfterViewInit,
  Component,
  OnInit,
  OnDestroy,
} from '@angular/core';
import { Title } from '@angular/platform-browser';
import { Utils } from './utils';
import { SeoService } from './services/seo.service';

@Component({
  selector: 'contact',
  templateUrl: './contact.component.html',
  styleUrls: ['./contact.component.scss'],
  standalone: false,
})
export class ContactComponent implements OnInit, AfterViewInit, OnDestroy {
  public phoneCall = Utils.phoneCall;
  public phoneNumber = Utils.phoneNumber;
  public email = Utils.email;
  public whatsapp = Utils.whatsapp;
  public whatsAppNumber = Utils.whatsAppNumber;

  private intersectionObserver?: IntersectionObserver;
  private kickHandler?: () => void;
  private prefersReducedMotion = false;

  public constructor(private titleService: Title, private seo: SeoService) {}

  public ngOnInit(): void {
    window.scrollTo(0, 0);
    this.titleService.setTitle('Blauwtand — Contact');
    this.seo.updateSeo({
      title: 'Contact Blauwtand - We spreken je graag',
      description:
        'Neem contact op met Blauwtand, jouw partner in IT recruitment gevestigd in Zoetermeer. Leer meer.',
    });
  }

  public async ngAfterViewInit(): Promise<void> {
    this.prefersReducedMotion = this.getPrefersReducedMotion();

    // Defer until layout has stabilized (iOS Safari can delay initial IntersectionObserver callbacks until a user gesture).
    await this.waitForAnimationFrames(2);
    this.setupScrollAnimations();
  }

  public ngOnDestroy(): void {
    this.intersectionObserver?.disconnect();
    this.removeKickReveal();
  }

  private setupScrollAnimations(): void {
    const animatedElements = Array.from(
      document.querySelectorAll<HTMLElement>('.animate-on-scroll')
    );

    if (animatedElements.length === 0) {
      return;
    }

    // Reduced motion: never hide content and skip observers.
    if (this.prefersReducedMotion) {
      for (const el of animatedElements) {
        el.classList.remove('animate-hidden');
      }
      return;
    }

    // If IntersectionObserver isn't available, fall back to immediately showing everything.
    if (typeof window === 'undefined' || typeof IntersectionObserver === 'undefined') {
      for (const el of animatedElements) {
        el.classList.remove('animate-hidden');
      }
      return;
    }

    this.intersectionObserver?.disconnect();

    // Create observer once; attach elements after an initial reveal pass.
    const observer = new IntersectionObserver(
      (entries) => {
        for (const entry of entries) {
          if (!entry.isIntersecting) {
            continue;
          }

          entry.target.classList.remove('animate-hidden');
          observer.unobserve(entry.target);
        }
      },
      {
        threshold: 0.12,
        rootMargin: '0px 0px -60px 0px',
      }
    );

    this.intersectionObserver = observer;

    // Safari/iOS quirk: initial IO callbacks can be delayed until a scroll/resize.
    // Also: removing the class in the same frame as layout can prevent transitions.
    requestAnimationFrame(() => {
      this.revealVisibleElements(animatedElements);

      const remainingElements = animatedElements.filter((el) =>
        el.classList.contains('animate-hidden')
      );

      for (const el of remainingElements) {
        observer.observe(el);
      }

      // One-time "kick": if callbacks are still delayed, a scroll/resize/orientation change will reveal.
      this.installKickReveal(animatedElements);
    });
  }

  private revealVisibleElements(elements: HTMLElement[]): void {
    // Keep the logic in sync with IO rootMargin bottom (-60px).
    const bottomOffsetPx = 60;
    const viewportHeight = window.innerHeight || 0;

    for (const el of elements) {
      if (!el.classList.contains('animate-hidden')) {
        continue;
      }

      const rect = el.getBoundingClientRect();
      const isInView = rect.top < viewportHeight - bottomOffsetPx && rect.bottom > 0;

      if (isInView) {
        el.classList.remove('animate-hidden');
      }
    }
  }

  private installKickReveal(elements: HTMLElement[]): void {
    this.removeKickReveal();

    const handler = () => {
      this.revealVisibleElements(elements);

      const anyHiddenLeft = elements.some((el) => el.classList.contains('animate-hidden'));
      if (!anyHiddenLeft) {
        this.removeKickReveal();
      }
    };

    this.kickHandler = handler;

    // Run immediately and after a short delay (covers late layout/font/image settling).
    setTimeout(handler, 0);
    setTimeout(handler, 250);

    window.addEventListener('scroll', handler, { passive: true });
    window.addEventListener('resize', handler, { passive: true });
    window.addEventListener('orientationchange', handler);
  }

  private removeKickReveal(): void {
    if (!this.kickHandler) {
      return;
    }

    window.removeEventListener('scroll', this.kickHandler);
    window.removeEventListener('resize', this.kickHandler);
    window.removeEventListener('orientationchange', this.kickHandler);
    this.kickHandler = undefined;
  }

  private getPrefersReducedMotion(): boolean {
    try {
      return window.matchMedia?.('(prefers-reduced-motion: reduce)')?.matches ?? false;
    } catch {
      return false;
    }
  }

  private waitForAnimationFrames(frameCount: number): Promise<void> {
    const frames = Math.max(1, Math.floor(frameCount));

    return new Promise((resolve) => {
      let remaining = frames;
      const step = () => {
        remaining -= 1;
        if (remaining <= 0) {
          resolve();
          return;
        }
        requestAnimationFrame(step);
      };

      requestAnimationFrame(step);
    });
  }

  public googleMapsOffice(): void {
    window.open(
      'https://www.google.com/maps/place/Blauwtand/@52.0420598,4.4682062,17z/data=!3m1!4b1!4m5!3m4!1s0x47c5c9419435827d:0xdc71f13147a8bac!8m2!3d52.0420565!4d4.4703949',
      '_blank'
    );
  }
}
